import React, { useState, useEffect, useCallback } from "react";
import { Form, FormGroup, Label, Input, Row, Col, Button, Modal, ModalHeader, ModalBody } from "reactstrap";
import defaultIMG from "../assets/img/default.png"; 
import "../assets/scss/SeriesForm.scss";

const definicioOptions = [
  { value: "1080p", label: "1080p" },
  { value: "720p", label: "720p" },
  { value: "[HDTV 720p]", label: "HDTV 720p" },
  { value: "[HDTV]", label: "HDTV" },
  { value: "4K", label: "4K Ultra HD" },
];

// Mapeo de géneros de TMDB (inglés y español) a catalán
const genreMapping = {
  // Inglés
  "Action & Adventure": "Acció",
  "Action": "Acció",
  "Adventure": "Aventura",
  "Animation": "Animació",
  "Comedy": "Comèdia",
  "Crime": "Thriller",
  "Documentary": "Documental",
  "Drama": "Drama",
  "Family": "Aventura",
  "Fantasy": "Fantasia",
  "Horror": "Terror",
  "Kids": "Animació",
  "Mystery": "Misteri",
  "News": "Documental",
  "Reality": "Documental",
  "Romance": "Romance",
  "Sci-Fi & Fantasy": "Ciència-ficció",
  "Science Fiction": "Ciència-ficció",
  "Soap": "Drama",
  "Talk": "Documental",
  "Thriller": "Thriller",
  "War & Politics": "Drama",
  "Western": "Aventura",
  // Español
  "Acción y Aventura": "Acció",
  "Animación": "Animació",
  "Comedia": "Comèdia",
  "Crimen": "Thriller",
  "Documental": "Documental",
  "Familia": "Aventura",
  "Fantasía": "Fantasia",
  "Misterio": "Misteri",
  "Película de TV": "Documental",
  "Ciencia ficción y fantasía": "Ciència-ficció",
  "Suspense": "Thriller",
  "Guerra y Política": "Drama",
};


const SeriesForm = ({ onSubmit, onCancel, isLoading, initialData }) => {
  const isEditing = !!initialData;

  const [formData, setFormData] = useState({
    nom: "",
    image: "",
    temporada: "",
    capitolsTemporada: "",
    capitol: "",
    definicio: "[HDTV 720p]",
    genero: "",
  });

  const [imagePreviewUrl, setImagePreviewUrl] = useState(defaultIMG);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  
  const [searchResults, setSearchResults] = useState([]);
  const [showSelector, setShowSelector] = useState(false);
  // Removed unused currentSeriesId
  
  // Track which fields were auto-filled by the API
  const [autoFilledFields, setAutoFilledFields] = useState({
      temporada: false,
      capitolsTemporada: false
  });
  
  // Ref to prevent auto-lookup from overwriting a manual selection
  const selectionLockRef = React.useRef(false);

  // definicioOptions moved outside

  const generoOptions = [
    { value: "", label: "Selecciona un gènere" },
    { value: "Drama", label: "Drama" },
    { value: "Comèdia", label: "Comèdia" },
    { value: "Acció", label: "Acció" },
    { value: "Ciència-ficció", label: "Ciència-ficció" },
    { value: "Terror", label: "Terror" },
    { value: "Thriller", label: "Thriller" },
    { value: "Fantasia", label: "Fantasia" },
    { value: "Documental", label: "Documental" },
    { value: "Animació", label: "Animació" },
    { value: "Romance", label: "Romance" },
    { value: "Aventura", label: "Aventura" },
    { value: "Misteri", label: "Misteri" },
  ];



  // Logic to fetch full series details (Seasons, Episodes, and Genre)
  const fetchSeriesDetails = useCallback(async (seriesId) => {
      const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY || "f090bb54758cabf231fb605d3e3e0468";
      try {
          const res = await fetch(`https://api.themoviedb.org/3/tv/${seriesId}?api_key=${TMDB_API_KEY}&language=es-ES`);
          if (res.ok) {
              const data = await res.json();
              const latestSeasonNum = data.number_of_seasons;
              
              // Find the total episodes of the latest season
              const latestSeason = data.seasons ? data.seasons.find(s => s.season_number === latestSeasonNum) : null;
              const episodeCount = latestSeason ? latestSeason.episode_count : 0;
              
              // Get primary genre
              let primaryGenre = "";
              if (data.genres && data.genres.length > 0) {
                  const tmdbGenre = data.genres[0].name;
                  primaryGenre = genreMapping[tmdbGenre] || tmdbGenre;
              }
              
              setFormData(prev => ({
                  ...prev,
                  temporada: latestSeasonNum || prev.temporada,
                  capitolsTemporada: episodeCount || prev.capitolsTemporada,
                  genero: primaryGenre || prev.genero
              }));
              
              setAutoFilledFields({
                  temporada: !!latestSeasonNum,
                  capitolsTemporada: !!episodeCount
              });
          }
      } catch (err) {
          console.error("Error fetching series details:", err);
      }
  }, []); // genreMapping is now constant outside (handled in next logic block)

  // Manual refresh genre from TMDB
  const handleRefreshGenre = async () => {
      if (!formData.nom || formData.nom.length < 3) {
          console.warn("Nombre de serie demasiado corto");
          return;
      }
      
      const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY || "f090bb54758cabf231fb605d3e3e0468";
      
      try {

          const query = encodeURIComponent(formData.nom);
          const res = await fetch(`https://api.themoviedb.org/3/search/tv?api_key=${TMDB_API_KEY}&query=${query}&language=es-ES&page=1&include_adult=false`);
          
          if (res.ok) {
              const data = await res.json();
              if (data.results && data.results.length > 0) {
                  const bestMatch = data.results[0];

                  
                  // Fetch detailed info
                  const detailRes = await fetch(`https://api.themoviedb.org/3/tv/${bestMatch.id}?api_key=${TMDB_API_KEY}&language=es-ES`);
                  if (detailRes.ok) {
                      const detailData = await detailRes.json();

                      
                      if (detailData.genres && detailData.genres.length > 0) {
                          const tmdbGenre = detailData.genres[0].name;
                          const mappedGenre = genreMapping[tmdbGenre];
                          

                          
                          if (mappedGenre) {
                              setFormData(prev => {
                                  const newData = { ...prev, genero: mappedGenre };

                                  return newData;
                              });
                              
                              // Verificar después de un momento
                              setTimeout(() => {

                              }, 100);
                          } else {
                              console.warn(`⚠️ Género "${tmdbGenre}" no tiene mapeo automático`);
                          }
                      } else {
                          console.warn("⚠️ Esta serie no tiene géneros en TMDB");
                      }
                  }
              } else {
                  console.warn("⚠️ No se encontró la serie en TMDB");
              }
          }
      } catch (err) {
          console.error("❌ Error fetching genre:", err);
      }
  };

  // --- Efecto 1: Inicializar formData cuando initialData cambia (al abrir modal, cambiar serie a editar) ---
  // Este efecto solo se encarga de llenar formData, NO de manejar la carga de imagen.
  useEffect(() => {
    if (isEditing) {
      // Modo Edición: Usar initialData para pre-llenar el formulario
      // Para capitol: mostrar solo el número de capítulo (últimos 2 dígitos)
      const currentCapitolNumber = Number(initialData.capitol) || 0;
      const chapterNumber = currentCapitolNumber % 100;
      
      setFormData({
        nom: initialData.nom || "",
        image: initialData.image || "", // Establecer la URL de la imagen en formData
        temporada: initialData.temporada || "",
        capitolsTemporada: initialData.capitolsTemporada || "",
        capitol: chapterNumber, // Mostrar solo el número de capítulo
        // Asegurarse de que la definición inicial sea una opción válida, sino default
        definicio: definicioOptions.some(
          (opt) => opt.value === initialData.definicio
        )
          ? initialData.definicio
          : definicioOptions[0]?.value || "",
        genero: initialData.genero || "",
        // Inicializar otros campos editables si los añades al formulario
        // link: initialData.link || "",
        // link720: initialData.link720 || "",
        // error: initialData.error || "",
        // missatge: initialData.missatge || "",
        // revisada: initialData.revisada !== undefined ? initialData.revisada : false,
        // activa: initialData.activa !== undefined ? initialData.activa : true,
      });
      // IMPORTANTE: No establecer imagePreviewUrl aquí. El segundo efecto lo hará basado en formData.image.
    } else {
      // Modo Añadir: Resetear a valores por defecto
      setFormData({
        nom: "",
        image: "",
        temporada: "",
        capitolsTemporada: "",
        capitol: "",
        definicio: "[HDTV 720p]",
        genero: "", // Default a la primera opción
        // link: "", link720: "", error: "", missatge: "", revisada: false, activa: true,
      });
      // IMPORTANTE: No establecer imagePreviewUrl aquí. El segundo efecto lo hará basado en formData.image.
    }
  }, [initialData, isEditing]); // Dependencia en initialData y isEditing

  // --- Efecto 2: Cargar y previsualizar la imagen cuando formData.image cambia ---
  useEffect(() => {
    const imageUrlToLoad = formData.image;

    if (!imageUrlToLoad || imageUrlToLoad === defaultIMG) {
      setImagePreviewUrl(defaultIMG);
      setIsImageLoaded(false);
      setImageLoading(false);
      return;
    }

    setImageLoading(true);
    setIsImageLoaded(false);

    const img = new Image();
    img.onload = () => {
      if (img.src === formData.image) {
        setImagePreviewUrl(formData.image);
        setIsImageLoaded(true);
      }
      setImageLoading(false);
    };

    img.onerror = () => {
      if (img.src === formData.image) {
        setImagePreviewUrl(defaultIMG);
        setIsImageLoaded(false);
      }
      setImageLoading(false);
    };

    img.src = imageUrlToLoad;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [formData.image]);

  // --- AUTOMATIC POSTER LOOKUP ---
  useEffect(() => {
    // If we just manually selected a result, don't auto-fetch again for this change
    if (selectionLockRef.current) {
        selectionLockRef.current = false;
        return;
    }

    const needsImage = !formData.image || formData.image === defaultIMG;
    if (isEditing && !needsImage) return; 
    
    if (!formData.nom || formData.nom.length < 3) {
        setSearchResults([]);
        return;
    }

    const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY || "f090bb54758cabf231fb605d3e3e0468";
    
    const timeoutId = setTimeout(async () => {
        try {
            setImageLoading(true);
            const query = encodeURIComponent(formData.nom);
            const res = await fetch(`https://api.themoviedb.org/3/search/tv?api_key=${TMDB_API_KEY}&query=${query}&language=es-ES&page=1&include_adult=false`);
            if (res.ok) {
                const data = await res.json();
                if (data.results && data.results.length > 0) {
                    setSearchResults(data.results); // Store all results
                    
                    // Filter matches that HAVE a poster_path
                    const withPoster = data.results.filter(s => s.poster_path);
                    const bestMatch = withPoster.length > 0 ? withPoster[0] : data.results[0];
                    
                    if (bestMatch.poster_path) {
                        const url = `https://image.tmdb.org/t/p/w500${bestMatch.poster_path}`;
                        // Lock before updating to prevent self-trigger loop
                        selectionLockRef.current = true;
                        setFormData(prev => ({ ...prev, image: url }));
                        
                        // Save series ID and fetch extra details for automation
                        // setCurrentSeriesId(bestMatch.id);
                        fetchSeriesDetails(bestMatch.id);
                    }
                } else {
                    setSearchResults([]);
                }
            }
        } catch (error) {
            console.error("Auto-poster fetch error:", error);
        } finally {
            setImageLoading(false);
        }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [formData.nom, isEditing, formData.image, fetchSeriesDetails]);

  const selectResult = (result) => {
      if (result.poster_path) {
          const url = `https://image.tmdb.org/t/p/w500${result.poster_path}`;
          selectionLockRef.current = true;
          setFormData(prev => ({ 
              ...prev, 
              nom: result.name || result.original_name,
              image: url 
          }));
          
          // Save series ID and fetch extra details
          // setCurrentSeriesId(result.id);
          fetchSeriesDetails(result.id);
      }
      setShowSelector(false);
  };

  // --- Handler para cambios en los inputs del formulario ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    
    // If user manually changes a field, remove the auto-filled highlight
    if (autoFilledFields[name]) {
        setAutoFilledFields(prev => ({ ...prev, [name]: false }));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  // --- Handler para el envío del formulario ---
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Construir el número completo: temporada * 100 + capítulo
    // Ejemplo: T2 Cap5 -> 205, T10 Cap12 -> 1012
    const season = Number(formData.temporada) || 1;
    const chapter = Number(formData.capitol) || 0;
    const capitolToStore = season * 100 + chapter;
    
    const dataToSubmit = {
      ...formData,
      capitol: capitolToStore
    };
    
    onSubmit(dataToSubmit);
  };

  // --- Renderizado ---
  return (
    <>
    <Form
      onSubmit={handleSubmit}
      className={`serie-form ${isEditing ? "edit-mode" : "add-mode"}`}
    >
      <Row className="gx-4">
        {/* Columna Izquierda: Póster */}
        <Col md={5} className="form-col-poster">
          <div className="form-illustration-container">
            <div className="illustration-wrapper">
              {imageLoading && (
                <div className="image-loading-spinner">
                  <i className="bi bi-arrow-repeat spin"></i>
                </div>
              )}
              <img
                src={imagePreviewUrl}
                alt={formData.nom || "Previsualització"}
                className="form-illustration-img"
                style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover', 
                    display: 'block'
                }}
              />
              {isImageLoaded &&
                imagePreviewUrl !== defaultIMG &&
                formData.nom && (
                  <>
                    <button 
                        type="button"
                        className="btn-refresh-poster" 
                        onClick={() => {
                            // Clear image to trigger the auto-fetch effect
                            setFormData(prev => ({ ...prev, image: "" }));
                        }}
                        title="Buscar portada vertical"
                    >
                        <i className="bi bi-arrow-clockwise"></i>
                    </button>
                  </>
                )}
            </div>
          </div>
          {!isEditing && (
              <p className="text-center text-muted small mt-2 mb-1">
                  <i className="bi bi-magic me-1"></i> 
                  Buscant portada automàticament...
              </p>
          )}
          
          {searchResults.length > 1 && !imageLoading && (
            <div className="text-center mt-1">
              <Button 
                color="link" 
                size="sm" 
                className="text-decoration-none p-0 text-primary"
                onClick={() => setShowSelector(true)}
              >
                <i className="bi bi-layers me-1"></i>
                No és esta sèrie?
              </Button>
            </div>
          )}
        </Col>

        {/* Columna Derecha: Campos */}
        <Col md={7} className="form-col-fields">
          {/* Nombre (Ancho total) */}
          <FormGroup className="mb-4">
            <Label for="nom">Nom de la sèrie</Label>
            <Input
              type="text"
              name="nom"
              id="nom"
              className="input-premium-lg"
              placeholder="Ej: The Mandalorian"
              value={formData.nom}
              onChange={handleChange}
              required
            />
          </FormGroup>

          {/* Cuadrícula para el resto de campos */}
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="temporada">Temporada actual</Label>
                <Input
                  type="number"
                  name="temporada"
                  id="temporada"
                  className={autoFilledFields.temporada ? "is-auto-populated" : ""}
                  placeholder="1"
                  value={formData.temporada}
                  onChange={handleChange}
                  required
                  min="1"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="capitol">Capítol vist</Label>
                <Input
                  type="number"
                  name="capitol"
                  id="capitol"
                  placeholder="0"
                  value={formData.capitol}
                  onChange={handleChange}
                  required
                  min="0"
                />
              </FormGroup>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={6}>
               <FormGroup>
                <Label for="capitolsTemporada">Total capítols</Label>
                <Input
                  type="number"
                  name="capitolsTemporada"
                  id="capitolsTemporada"
                  className={autoFilledFields.capitolsTemporada ? "is-auto-populated" : ""}
                  placeholder="8"
                  value={formData.capitolsTemporada}
                  onChange={handleChange}
                  required
                  min="1"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="definicio">Qualitat</Label>
                <Input
                  type="select"
                  name="definicio"
                  id="definicio"
                  value={formData.definicio}
                  onChange={handleChange}
                  required
                >
                  {definicioOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={12}>
              <FormGroup>
                <Label for="genero">Gènere</Label>
                <div className="d-flex">
                    <Input
                      type="select"
                      name="genero"
                      id="genero"
                      value={formData.genero}
                      onChange={handleChange}
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    >
                      {generoOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                    {formData.nom && formData.nom.length >= 3 && (
                        <Button
                          color="info"
                          outline
                          onClick={handleRefreshGenre}
                          type="button"
                          title="Auto-detectar gènere des de TMDB"
                          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0 }}
                        >
                          <i className="bi bi-magic"></i>
                        </Button>
                    )}
                </div>
              </FormGroup>
            </Col>
          </Row>

          {/* Botones de acción integrados en la columna de campos para mejor flujo */}
          <div className="form-actions-inline mt-5 pt-3 border-top d-flex gap-3 justify-content-end">
                  <Button
                      color="secondary"
                      outline
                      onClick={onCancel}
                      className="px-4 rounded-pill border-0 shadow-sm"
                      disabled={isLoading}
                  >
                      Cancel·lar
                  </Button>
                  <Button
                      color="primary"
                      type="submit"
                      className="px-5 rounded-pill btn-premium border-0"
                      disabled={isLoading || imageLoading}
                  >
                      {isLoading ? "Guardant..." : isEditing ? "Guardar Canvis" : "Afegir Sèrie"}
                  </Button>
          </div>
        </Col>
      </Row>
    </Form>

    {/* Modal de Selección de Alternativas */}
    <Modal isOpen={showSelector} toggle={() => setShowSelector(false)} centered className="series-selector-modal">
        <ModalHeader toggle={() => setShowSelector(false)}>
            Selecciona la sèrie correcta
        </ModalHeader>
        <ModalBody>
            <div className="selection-list">
                {searchResults.map((result) => (
                    <div 
                        key={result.id} 
                        className="selection-item d-flex gap-3 align-items-center"
                        onClick={() => selectResult(result)}
                    >
                        <div className="selection-poster">
                            <img 
                                src={result.poster_path ? `https://image.tmdb.org/t/p/w200${result.poster_path}` : defaultIMG} 
                                alt={result.name}
                                style={{ width: '60px', borderRadius: '4px' }}
                            />
                        </div>
                        <div className="selection-info">
                            <div className="fw-bold">{result.name || result.original_name}</div>
                            <div className="text-muted small">
                                {result.first_air_date ? new Date(result.first_air_date).getFullYear() : 'Any desconegut'}
                            </div>
                        </div>
                        <div className="ms-auto">
                            <i className="bi bi-chevron-right text-muted"></i>
                        </div>
                    </div>
                ))}
            </div>
        </ModalBody>
    </Modal>
    </>
  );
};

export default SeriesForm;
